// src/services/ezer/EzerSmartSuggestions.js
//
// Inspects Vuex store data (already fetched by App.vue / Dashboard)
// and returns role-appropriate proactive alert suggestions.
//
// Each alert: { id, priority, icon, title, description, action }
//   priority: 'high' | 'medium' | 'low'
//   action: string sent to the chatbot when the user clicks it

import api from '@/config/api'
import { getLocationId } from '@/utils/SessionDataHandler'

/**
 * Gather smart alerts for the current user.
 * @param {Object} store – Vuex store instance
 * @returns {Promise<Array>} alerts sorted by priority
 */
export async function fetchSmartAlerts(store) {
  const alerts = []
  const session = store.getters.stSessionData || {}
  const personId = session.personId || session.personid
  const locationId = session.locationid || getLocationId()

  if (!personId || !locationId) return alerts

  // Determine role from permissions
  const permData = store.getters.stFullPermissionData || {}
  const adminCards = permData.cardslist?.adminMenuCards || []
  const isAdmin = adminCards.length > 0

  // ── Volunteer alerts (always run — admins are also volunteers) ──
  try {
    await gatherVolunteerAlerts(store, alerts, personId, locationId)
  } catch (e) {
    console.warn('Ezer: volunteer alert check failed', e)
  }

  // ── Admin-only alerts ──
  if (isAdmin) {
    try {
      await gatherAdminAlerts(store, alerts, locationId)
    } catch (e) {
      console.warn('Ezer: admin alert check failed', e)
    }
  }

  // Sort: high → medium → low
  const order = { high: 0, medium: 1, low: 2 }
  alerts.sort((a, b) => (order[a.priority] ?? 9) - (order[b.priority] ?? 9))

  return alerts
}

// ─────────────────────────────────────────────────────────────────────
// Volunteer checks
// ─────────────────────────────────────────────────────────────────────
async function gatherVolunteerAlerts(store, alerts, personId, locationId) {
  // 1. Pending swap requests
  const swaps = store.getters.stPersonPendingSwapRequests
  if (swaps && Array.isArray(swaps) && swaps.length > 0) {
    alerts.push({
      id: 'vol-pending-swaps',
      priority: 'high',
      icon: 'swap_horiz',
      title: `${swaps.length} pending swap request${swaps.length > 1 ? 's' : ''}`,
      description: 'You have swap requests waiting for your response.',
      action: 'Show my pending swap requests',
    })
  }

  // 2. Unconfirmed assignments
  const assignments = store.getters.stUserAssignments
  if (assignments && typeof assignments === 'object') {
    const unconfirmed = assignments.unconfirmed_count
      ?? assignments.unconfirmedUpcomingAssignments
      ?? 0
    if (unconfirmed > 0) {
      alerts.push({
        id: 'vol-unconfirmed',
        priority: 'high',
        icon: 'event_available',
        title: `${unconfirmed} unconfirmed assignment${unconfirmed > 1 ? 's' : ''}`,
        description: 'Please confirm or decline your upcoming assignments.',
        action: 'Show my unconfirmed assignments',
      })
    }
  }

  // 3. No assigned skills — lightweight check via API
  try {
    const resp = await api.get('/manage-user/person-skills', {
      params: { locationid: locationId, personid: personId, page_size: 1, page_number: 1 },
    })
    const total = resp.data?.pagination?.total_records ?? resp.data?.data?.length ?? -1
    if (total === 0) {
      alerts.push({
        id: 'vol-no-skills',
        priority: 'medium',
        icon: 'school',
        title: 'No skills assigned',
        description: 'Add skills to your profile so you can be scheduled.',
        action: 'Help me add skills to my profile',
      })
    }
  } catch {
    // Silently skip — non-critical
  }

  // 4. Frequent declined assignments → suggest preferences / rules
  await checkDeclinedAssignments(store, alerts, personId, locationId)

  // 5. Open sign-up opportunities
  const signupCount = store.getters.stSignupCount
  const openings = typeof signupCount === 'object'
    ? (signupCount.total_openings ?? signupCount.signup_count ?? 0)
    : (Number(signupCount) || 0)
  if (openings > 0) {
    alerts.push({
      id: 'vol-open-signups',
      priority: 'low',
      icon: 'how_to_reg',
      title: `${openings} open sign-up opportunit${openings > 1 ? 'ies' : 'y'}`,
      description: 'There are positions available you can volunteer for.',
      action: 'Show me open sign-up opportunities',
    })
  }
}

// ─────────────────────────────────────────────────────────────────────
// Declined assignments → preferences / rules
// ─────────────────────────────────────────────────────────────────────
async function checkDeclinedAssignments(store, alerts, personId, locationId) {
  try {
    // Use the monthly assignment-status breakdown already fetched by dashboard
    const statusByMonth = store.getters.stPersonAssignmentStatusCountByMonth
    let recentDeclines = 0

    if (Array.isArray(statusByMonth)) {
      // Sum declined counts from the most recent 1-2 entries (≈ past 2 weeks to a month)
      const recent = statusByMonth.slice(-2)
      recentDeclines = recent.reduce(
        (sum, m) => sum + (m.declined_count ?? m.declinedcount ?? m.declined ?? 0),
        0,
      )
    }

    // If store data unavailable, make a lightweight API call
    if (!recentDeclines) {
      const resp = await api.get('/manage-user/person-assignments', {
        params: {
          personid: personId,
          locationid: locationId,
          status: 'declined',
          page_size: 1,
          page_number: 1,
        },
      })
      recentDeclines = resp.data?.pagination?.total_records ?? 0
    }

    if (recentDeclines >= 3) {
      // Check whether the volunteer already has scheduling rules
      let hasRules = false
      try {
        const rulesResp = await api.get(
          `/users/${personId}/scheduling-rules/${locationId}`,
        )
        const totalRules =
          rulesResp.data?.output?.scheduling_preferences?.total_rules ?? 0
        hasRules = totalRules > 0
      } catch {
        // If endpoint fails, still show the suggestion
      }

      alerts.push({
        id: 'vol-declined-assignments',
        priority: 'medium',
        icon: 'tune',
        title: `${recentDeclines} declined assignment${recentDeclines > 1 ? 's' : ''} recently`,
        description: hasRules
          ? 'You\'ve declined several assignments. Want to update your scheduling preferences or rules?'
          : 'Set up your scheduling preferences and rules so you only get assignments that work for you.',
        action: hasRules
          ? 'Help me update my scheduling preferences and rules'
          : 'Help me set up my scheduling preferences and rules',
      })
    }
  } catch {
    // Non-critical — skip
  }
}

// ─────────────────────────────────────────────────────────────────────
// Admin checks
// ─────────────────────────────────────────────────────────────────────
async function gatherAdminAlerts(store, alerts, locationId) {
  // Run all admin checks in parallel
  const results = await Promise.allSettled([
    checkOpenSlots(alerts, locationId),
    checkAdminPendingSwaps(store, alerts, locationId),
    checkPendingRegistrations(store, alerts, locationId),
    checkPendingSkillInterests(alerts, locationId),
    checkLowCredit(store, alerts, locationId),
  ])
  results.forEach((r) => {
    if (r.status === 'rejected') console.warn('Ezer admin alert check failed:', r.reason)
  })
}

// 1. Open slots in upcoming 2-3 week activity instances
async function checkOpenSlots(alerts, locationId) {
  try {
    const resp = await api.get('/activity-instance-workgroups/needing-volunteers', {
      params: { location_id: locationId },
    })
    const data = resp.data?.data || resp.data
    const count = data?.total_count || 0
    if (count > 0) {
      alerts.push({
        id: 'admin-open-slots',
        priority: 'high',
        icon: 'group_add',
        title: `${count} workgroup${count > 1 ? 's' : ''} need${count === 1 ? 's' : ''} volunteers`,
        description: 'Upcoming activity instances have unfilled positions.',
        action: 'Show me workgroups that need volunteers',
      })
    }
  } catch {
    // Endpoint may not be available
  }
}

// 2. Multiple pending swap requests (admin view)
async function checkAdminPendingSwaps(store, alerts, locationId) {
  // Try store first (already fetched by dashboard)
  let count = store.getters.stLocationPendingSwapRequests?.total_pending_requests
  if (count === undefined || count === null) {
    try {
      const resp = await api.post('/managelocationajax', {
        cmd: 'getlocationpendingswapcount',
        locationid: locationId,
      })
      const output = resp.data?.output
      count = Array.isArray(output) ? output[0]?.total_pending_requests : output?.total_pending_requests
    } catch {
      return
    }
  }
  if (count && count > 1) {
    alerts.push({
      id: 'admin-pending-swaps',
      priority: 'medium',
      icon: 'swap_horiz',
      title: `${count} pending swap requests`,
      description: 'Volunteers have unresolved swap requests.',
      action: 'Review pending swap requests',
    })
  }
}

// 3. Pending registrations
async function checkPendingRegistrations(store, alerts, locationId) {
  let count = store.getters.stPendingRegistrations?.pending_registration_count
  if (count === undefined || count === null) {
    try {
      const resp = await api.post('/managelocationajax', {
        cmd: 'getpendingregistrationscount',
        locationid: locationId,
      })
      count = resp.data?.pending_registration_count ?? resp.data?.output?.pending_registration_count
    } catch {
      return
    }
  }
  if (count && count > 0) {
    alerts.push({
      id: 'admin-pending-regs',
      priority: 'medium',
      icon: 'person_add',
      title: `${count} pending registration${count > 1 ? 's' : ''}`,
      description: 'New users are waiting for approval.',
      action: 'Show pending registrations',
    })
  }
}

// 4. Pending skill interest requests
async function checkPendingSkillInterests(alerts, locationId) {
  try {
    const resp = await api.get('/skills/pending-interest-requests', {
      params: { location_id: locationId },
    })
    const data = resp.data?.data || resp.data
    const count = data?.total_count || 0
    if (count > 0) {
      alerts.push({
        id: 'admin-skill-interests',
        priority: 'low',
        icon: 'pending_actions',
        title: `${count} pending skill interest request${count > 1 ? 's' : ''}`,
        description: 'Volunteers want to be added to new skills.',
        action: 'Review pending skill interest requests',
      })
    }
  } catch {
    // Endpoint may not be available
  }
}

// 5. Low credit / balance
async function checkLowCredit(store, alerts, locationId) {
  let balance = store.getters.stLocationBalance
  if (!balance) {
    try {
      const resp = await api.get(`/billing/location-balance/${locationId}`)
      balance = resp.data
    } catch {
      return
    }
  }
  const credit = balance?.available_credit ?? balance?.balance ?? balance?.credit
  if (credit !== undefined && credit !== null && Number(credit) < 5) {
    alerts.push({
      id: 'admin-low-credit',
      priority: 'high',
      icon: 'account_balance_wallet',
      title: `Low credit: $${Number(credit).toFixed(2)}`,
      description: 'Your account balance is low. Top up to avoid service interruptions.',
      action: 'Check my billing and balance',
    })
  }
}
